import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import Pagination from "../components/Pagination";
import { Checkbox } from "../components/ui/checkbox";
import {
  UserPlus, Spinner, Pencil, Trash, ShieldCheck, Buildings, Plus, UserCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getErrorMessage } from "../lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERM_GROUPS = [
  {
    labelKey: "staff.device_control",
    perms: [
      { key: "device_control", labelKey: "staff_perm.control" },
      { key: "device_add", labelKey: "staff_perm.device_add" },
      { key: "device_edit", labelKey: "staff_perm.device_edit" },
      { key: "device_reassign", labelKey: "staff_perm.device_reassign" },
    ],
  },
  {
    labelKey: "staff.balance_ops",
    perms: [
      { key: "topup_load", labelKey: "topup.title" },
      { key: "topup_clear", labelKey: "staff_perm.balance_clear" },
      { key: "transfer_approve", labelKey: "staff_perm.transfer_approve" },
      { key: "tariff_set", labelKey: "staff_perm.tariff_set" },
      { key: "payment_cash", label: "Nakit Ödeme Al" },
      { key: "payment_bank", label: "Havale Ödeme Al" },
    ],
  },
  {
    labelKey: "staff.tenant_mgmt",
    perms: [
      { key: "tenant_add", labelKey: "staff_perm.tenant_add" },
      { key: "tenant_edit", labelKey: "staff_perm.tenant_edit" },
      { key: "tenant_remove", labelKey: "units.remove_tenant" },
      { key: "tenant_delete", labelKey: "staff_perm.tenant_delete" },
    ],
  },
  {
    labelKey: "staff.apartment_unit",
    perms: [
      { key: "apartment_add", labelKey: "apartments.add" },
      { key: "apartment_edit", labelKey: "staff_perm.apartment_edit" },
      { key: "apartment_delete", labelKey: "staff_perm.apartment_delete" },
      { key: "unit_add", labelKey: "staff_perm.unit_add" },
      { key: "unit_edit", labelKey: "staff_perm.unit_edit" },
      { key: "unit_delete", labelKey: "staff_perm.unit_delete" },
    ],
  },
  {
    labelKey: "staff.finance_reports",
    perms: [
      { key: "reports_view", labelKey: "staff_perm.reports_view" },
      { key: "fixed_expense_add", labelKey: "staff_perm.fixed_expense_add" },
      { key: "fixed_expense_edit", labelKey: "staff_perm.fixed_expense_edit" },
      { key: "fixed_expense_delete", labelKey: "staff_perm.fixed_expense_delete" },
      { key: "fixed_expense_exempt", labelKey: "staff_perm.exemption_manage" },
      { key: "invoice_delete", labelKey: "invoices.delete" },
    ],
  },
  {
    labelKey: "staff.system",
    perms: [
      { key: "settings_edit", labelKey: "staff_perm.settings_edit" },
    ],
  },
];

const StaffPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [apartments, setApartments] = useState([]);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", phone: "" });
  const [formPerms, setFormPerms] = useState({});
  const [formApartments, setFormApartments] = useState([]);
  const [restrictApartments, setRestrictApartments] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff`);
      setStaff(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  const fetchApartments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/apartments`);
      setApartments(res.data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStaff(); fetchApartments(); }, [fetchStaff, fetchApartments]);

  const initPerms = () => {
    const p = {};
    PERM_GROUPS.forEach(g => g.perms.forEach(pm => { p[pm.key] = false; }));
    p.device_control = true;
    p.topup_load = true;
    p.reports_view = true;
    return p;
  };

  const openCreateDialog = () => {
    setFormData({ name: "", email: "", password: "", phone: "" });
    setFormPerms(initPerms());
    setFormApartments([]);
    setRestrictApartments(false);
    setDialogOpen(true);
  };

  const openEditDialog = (s) => {
    setEditingStaff(s);
    setFormData({ name: s.name, email: s.email, password: "", phone: s.phone || "" });
    setFormPerms(s.permissions || initPerms());
    const aptIds = s.assigned_apartment_ids || [];
    setFormApartments(aptIds);
    setRestrictApartments(aptIds.length > 0);
    setEditDialog(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/api/staff`, {
        ...formData,
        permissions: formPerms,
        assigned_apartment_ids: restrictApartments ? formApartments : [],
      });
      toast.success(t("staff.created"));
      setDialogOpen(false);
      fetchStaff();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setFormLoading(false); }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingStaff) return;
    setFormLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        permissions: formPerms,
        assigned_apartment_ids: restrictApartments ? formApartments : [],
      };
      if (formData.password) payload.password = formData.password;
      await axios.put(`${API_URL}/api/staff/${editingStaff.id}`, payload);
      toast.success(t("staff.updated"));
      setEditDialog(false);
      fetchStaff();
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/staff/${id}`);
      toast.success(t("staff.deleted"));
      fetchStaff();
    } catch { toast.error("Silme basarisiz"); }
  };

  const handleToggleActive = async (s) => {
    try {
      await axios.put(`${API_URL}/api/staff/${s.id}`, { is_active: !s.is_active });
      toast.success(s.is_active ? "Calisan devre disi birakildi" : "Calisan aktif edildi");
      fetchStaff();
    } catch { toast.error("Islem basarisiz"); }
  };

  const togglePerm = (key) => {
    setFormPerms(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleApartment = (aptId) => {
    setFormApartments(prev =>
      prev.includes(aptId) ? prev.filter(id => id !== aptId) : [...prev, aptId]
    );
  };

  const activePermCount = (perms) => {
    if (!perms) return 0;
    return Object.values(perms).filter(Boolean).length;
  };

  const PermCheckboxGrid = () => (
    <div className="space-y-4 max-h-[280px] overflow-y-auto pr-1">
      {PERM_GROUPS.map((group) => (
        <div key={group.labelKey}>
          <p className="text-xs font-bold text-slate-500 uppercase mb-2">{t(group.labelKey)}</p>
          <div className="grid grid-cols-2 gap-2">
            {group.perms.map((pm) => (
              <label key={pm.key} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
                <Checkbox checked={!!formPerms[pm.key]} onCheckedChange={() => togglePerm(pm.key)} data-testid={`perm-${pm.key}`} />
                {pm.label || t(pm.labelKey)}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const ApartmentCheckboxGrid = () => (
    <div className="space-y-2 max-h-[150px] overflow-y-auto pr-1">
      {apartments.map((a) => (
        <label key={a.id} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
          <Checkbox checked={formApartments.includes(a.id)} onCheckedChange={() => toggleApartment(a.id)} data-testid={`apt-${a.id}`} />
          <Buildings size={14} className="text-slate-400" /> {a.name}
        </label>
      ))}
    </div>
  );

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-900">{t("staff.title")}</h1>
          <p className="text-slate-500 mt-1">{staff.length} calisan kayitli</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-staff-btn">
          <UserPlus size={16} className="mr-2" /> Calisan Ekle
        </Button>
      </div>

      {/* Staff Table */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0">
          {staff.length === 0 ? (
            <div className="py-16 text-center">
              <UserCircle size={64} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Henuz calisan eklenmemis</h3>
              <p className="text-slate-500">Calisanlarinizi ekleyerek yetki verin</p>
            </div>
          ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("common.name")}</TableHead>
                    <TableHead>{t("common.email")}</TableHead>
                    <TableHead>{t("common.phone")}</TableHead>
                    <TableHead>{t("staff.permissions")}</TableHead>
                    <TableHead>{t("staff.apartments_header")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {staff.slice((page - 1) * pageSize, page * pageSize).map((s) => (
                    <TableRow key={s.id} data-testid={`staff-row-${s.id}`}
                      className={!s.is_active ? "opacity-50 bg-slate-50" : ""}>
                      <TableCell className="font-medium">{s.name}</TableCell>
                      <TableCell className="text-sm text-slate-500">{s.email}</TableCell>
                      <TableCell className="text-sm text-slate-500">{s.phone || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          <ShieldCheck size={12} className="mr-1 text-indigo-600" />
                          {activePermCount(s.permissions)} yetki
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {(s.assigned_apartment_ids || []).length > 0 ? (
                          <div className="flex gap-1 flex-wrap">
                            {(s.assigned_apartments || []).map(a => (
                              <Badge key={a.id} className="bg-slate-100 text-slate-700 text-[10px]">{a.name}</Badge>
                            ))}
                          </div>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">{t("fixed_expenses.all_apartments")}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Switch checked={s.is_active} onCheckedChange={() => handleToggleActive(s)}
                          data-testid={`toggle-active-${s.id}`} />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-amber-600"
                            onClick={() => openEditDialog(s)} data-testid={`edit-staff-${s.id}`}>
                            <Pencil size={14} />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600" data-testid={`delete-staff-${s.id}`}>
                                <Trash size={14} />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{t("staff.delete_title")}</AlertDialogTitle>
                                <AlertDialogDescription>{s.name} calisanini silmek istediginize emin misiniz?</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(s.id)} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <Pagination total={staff.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
          </>
          )}
        </CardContent>
      </Card>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("staff.add_title")}</DialogTitle>
            <DialogDescription>{t("staff.add_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("common.name")}</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="staff-name" />
                </div>
                <div className="space-y-1">
                  <Label>{t("common.email")}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="staff-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("common.password")}</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} required data-testid="staff-password" />
                </div>
                <div className="space-y-1">
                  <Label>{t("common.phone")}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="staff-phone" />
                </div>
              </div>
              {/* Permissions */}
              <div className="border-t pt-3 space-y-2">
                <p className="font-medium text-sm text-slate-900">{t("staff.permissions")}</p>
                <PermCheckboxGrid />
              </div>
              {/* Apartment restriction */}
              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-slate-900">Apartman Kisitlamasi</p>
                  <Switch checked={restrictApartments} onCheckedChange={setRestrictApartments} data-testid="restrict-apartments-toggle" />
                </div>
                {restrictApartments && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Calisan sadece secili apartmanlari gorebilir</p>
                    <ApartmentCheckboxGrid />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="create-staff-submit">
                {formLoading ? <Spinner size={14} className="animate-spin mr-1" /> : <UserPlus size={14} className="mr-1" />}
                Olustur
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Staff Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("staff.edit_title")}</DialogTitle>
            <DialogDescription>{editingStaff?.name}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="max-h-[60vh] overflow-y-auto space-y-4 pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("common.name")}</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="edit-staff-name" />
                </div>
                <div className="space-y-1">
                  <Label>{t("common.email")}</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="edit-staff-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>{t("staff.new_password_hint")}</Label>
                  <Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} data-testid="edit-staff-password" />
                </div>
                <div className="space-y-1">
                  <Label>{t("common.phone")}</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="edit-staff-phone" />
                </div>
              </div>
              <div className="border-t pt-3 space-y-2">
                <p className="font-medium text-sm text-slate-900">{t("staff.permissions")}</p>
                <PermCheckboxGrid />
              </div>
              <div className="border-t pt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-sm text-slate-900">Apartman Kisitlamasi</p>
                  <Switch checked={restrictApartments} onCheckedChange={setRestrictApartments} data-testid="edit-restrict-apartments" />
                </div>
                {restrictApartments && <ApartmentCheckboxGrid />}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialog(false)}>{t("common.cancel")}</Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={formLoading} data-testid="update-staff-submit">
                {formLoading ? <Spinner size={14} className="animate-spin mr-1" /> : null}
                Guncelle
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StaffPage;
